import React, { Fragment, useState } from 'react';
import * as Yup from 'yup';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CashManagementCrumbs } from 'front-office/constants/cash-management-crumbs';
import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  useEventCallback,
} from '@mui/material';
import { DatePicker } from '@mui/lab';
import { PmiButton } from 'shell/components/shared/button';
import PmiTextField from 'shell/components/shared/text-field';
import { DateTime } from 'luxon';
import { FormikErrors, useFormik } from 'formik';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useNavigate } from 'react-router-dom';
import ServiceList from 'front-office/components/cash-management/shared/service-list';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
// import { openPdf } from 'shell/utils/open-pdf';
import { Service } from 'shell/models/service';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseCertificateGeneric } from 'shell/models/response-certificate-generic';

export interface CheckLastMonthForm {
  service?: string;
  serviceDesc?: string;
  extractionDateFrom?: DateTime;
  extractionDateTo?: DateTime;
  closeCash?: boolean;
}

const CashManagementCheckLastMonth = (): React.ReactElement => {
  const printSummary = useHttpClient<HttpResponse<ResponseCertificateGeneric>>(
    `${__CERTIFICATION_API_URL__}/certificazione/controllo-cassa/stampa-mese-precedente`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const navigate = useNavigate();

  const { userProfile } = useUserProfile();

  /**
   * Se operatore Flag autorizzazione STAMPA CONTROLLO DI CASSA = ‘0’ oppure ‘1’
   * il Servizio deve essere valorizzato con il codice del Servizio Operatore e non digitabile
   * Se operatore Flag autorizzazione STAMPA CONTROLLO DI CASSA = ‘2’ il Servizio è digitabile.
   */

  const codeControlloCassa = userProfile.datiLogin?.profile?.feature?.controlloDiCassa || '';
  const userServiceCode = userProfile.datiLogin?.profile?.servizio?.key?.codiceServizio || '';
  const userServiceDesc = userProfile.datiLogin?.profile?.servizio?.descrizioneBreve || '';

  const disableService = codeControlloCassa !== '2';

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const serviceDefaultValue = ['0', '1'].indexOf(codeControlloCassa) >= 0 ? userServiceCode : '';
  const serviceDescDefaultValue =
    ['0', '1'].indexOf(codeControlloCassa) >= 0 ? userServiceDesc : '';

  const onSubmit = (form: CheckLastMonthForm) => {
    (
      printSummary({
        data: {
          chiusuraDiCassa: form.closeCash,
          codiceServizio: form.service,
          dataEstrazioneA: form.extractionDateTo?.toFormat('yyyy-MM-dd'),
          dataEstrazioneDa: form.extractionDateFrom?.toFormat('yyyy-MM-dd'),
          descrizioneBreveServizio: form.serviceDesc,
        },
      }) as ResponsePromise<HttpResponse<ResponseCertificateGeneric>>
    ).then((response) => {
      setPdfModal({
        data: response?.data?.payload?.contenutoPdfStampa || '',
        name: 'Gestione_Cassa',
        open: true,
      });
    });
  };
  const checkLastMonthForm = useFormik({
    initialValues: {
      service: serviceDefaultValue,
      serviceDesc: serviceDescDefaultValue,
      operator: '',
      extractionDateFrom: DateTime.local().minus({ month: 1 }).startOf('month'),
      extractionDateTo: DateTime.local().minus({ month: 1 }).endOf('month'),
      closeCash: false,
    },
    onSubmit,
    validationSchema: Yup.object().shape({
      service: Yup.string().required('Il campo è obbligatorio'),
      extractionDateFrom: Yup.date()
        .typeError('Inserire una data valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      extractionDateTo: Yup.date()
        .typeError('Inserire una data valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      closeCash: Yup.bool().required('Il campo è obbligatorio'),
    }),
  });

  const onChangeServiceList = useEventCallback((service: Service | Service[]) => {
    if (!Array.isArray(service)) {
      checkLastMonthForm.setValues({
        ...checkLastMonthForm.values,
        service: service.key.codiceServizio,
        serviceDesc: service.descrizioneBreve,
      });
    }
  });

  return (
    <Fragment>
      <Breadcrumbs
        items={[...CashManagementCrumbs, { name: 'Controllo/chiusura mese precedente' }]}
      />
      <Card raised className="pmi-my-3">
        <form onSubmit={checkLastMonthForm.handleSubmit}>
          <CardContent style={{ padding: '24px' }}>
            <Typography gutterBottom variant="h5" component="h2">
              Controllo/chiusura mese precedente per servizio
            </Typography>
            <div style={{ padding: '24px 0 0 0' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  {disableService ? (
                    <PmiTextField
                      id="service"
                      value={checkLastMonthForm.values.serviceDesc}
                      fullWidth
                      label="Servizio"
                      disabled
                    />
                  ) : (
                    <ServiceList
                      auth
                      value={checkLastMonthForm.values.service}
                      onChange={onChangeServiceList}
                      disabled={disableService}
                      error={!!checkLastMonthForm.errors.service}
                      errorMessage={checkLastMonthForm.errors.service}
                    />
                  )}
                </Grid>
                <Grid item xs={12} container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <DatePicker
                      label="Data inizio validità"
                      aria-label="Data inizio validità"
                      value={checkLastMonthForm.values.extractionDateFrom || null}
                      onChange={(date) => {
                        checkLastMonthForm.setFieldValue('extractionDateFrom', date);
                      }}
                      renderInput={(params) => (
                        <PmiTextField
                          id="extractionDateFrom"
                          {...params}
                          error={
                            checkLastMonthForm.touched.extractionDateFrom &&
                            !!checkLastMonthForm.errors.extractionDateFrom
                          }
                          helperText={
                            checkLastMonthForm.errors.extractionDateFrom as FormikErrors<string>
                          }
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <DatePicker
                      label="Data fine validità"
                      aria-label="Data fine validità"
                      value={checkLastMonthForm.values.extractionDateTo || null}
                      onChange={(date) => {
                        checkLastMonthForm.setFieldValue('extractionDateTo', date);
                      }}
                      renderInput={(params) => (
                        <PmiTextField
                          {...params}
                          id="extractionDateTo"
                          fullWidth
                          error={
                            checkLastMonthForm.touched.extractionDateTo &&
                            !!checkLastMonthForm.errors.extractionDateTo
                          }
                          helperText={
                            checkLastMonthForm.errors.extractionDateTo as FormikErrors<string>
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="closeCash"
                          name="closeCash"
                          checked={checkLastMonthForm.values.closeCash}
                          onChange={checkLastMonthForm.handleChange}
                        />
                      }
                      label="Chiusura di cassa"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </CardContent>
          <CardActions style={{ padding: '24px 24px 16px 24px' }}>
            <PmiButton id="submitForm" variant="contained" color="primary" type="submit">
              Conferma
            </PmiButton>
            <PmiButton
              id="goBack"
              variant="outlined"
              color="cdmBrand"
              onClick={() => {
                navigate('/sportello/gestione-cassa');
              }}
            >
              Annulla
            </PmiButton>
          </CardActions>
        </form>
      </Card>
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title="Gestione cassa"
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
        }}
      />
    </Fragment>
  );
};

CashManagementCheckLastMonth.displayName = 'Gestione cassa controllo/chiusura mese precedente';

export default CashManagementCheckLastMonth;
